// src/products/dataroom/components/SetupWizard/PartySetupStep.js
import React, { useState, useEffect } from 'react';
import { Plus, Building2, Trash2, CheckCircle } from 'lucide-react';
import partyTeamService from '../../services/partyTeamService';

const PartySetupStep = ({ projectId, setupData, onComplete }) => {
  const [selectedParties, setSelectedParties] = useState([]);
  const [newPartyName, setNewPartyName] = useState('');
  const [newPartyCode, setNewPartyCode] = useState('');
  const [newPartyColor, setNewPartyColor] = useState('#3B82F6');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const predefinedColors = [
    '#3B82F6', // Blue
    '#EF4444', // Red
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#F97316', // Orange
    '#6B7280'  // Gray
  ];

  useEffect(() => {
    // Initialize with existing data if any
    if (setupData.parties && setupData.parties.length > 0) {
      setSelectedParties(setupData.parties);
    }
  }, [setupData]);

  const handleAddParty = () => {
    if (newPartyName.trim() && newPartyCode.trim()) {
      // Check if code is already used
      const codeExists = selectedParties.some(party => party.code === newPartyCode.trim().toUpperCase());
      if (codeExists) {
        setError('This code is already used. Please choose a different 3-letter/digit code.');
        return;
      }

      // Check if color is already used
      const colorExists = selectedParties.some(party => party.color === newPartyColor);
      if (colorExists) {
        setError('This color is already used by another party. Please choose a different color.');
        return;
      }

      // Validate code format (3 characters, alphanumeric)
      const codePattern = /^[A-Z0-9]{3}$/;
      if (!codePattern.test(newPartyCode.trim().toUpperCase())) {
        setError('Code must be exactly 3 letters or digits (e.g., HTT, TB1, ABC).');
        return;
      }

      const newParty = {
        id: `party_${Date.now()}`,
        name: newPartyName.trim(),
        code: newPartyCode.trim().toUpperCase(),
        color: newPartyColor,
        party_type: 'custom',
        type: 'custom',
        description: 'Custom party'
      };
      setSelectedParties(prev => [...prev, newParty]);
      setNewPartyName(''); // Clear the inputs
      setNewPartyCode('');
      
      // Auto-select next available color
      const nextColor = predefinedColors.find(c => 
        !selectedParties.some(p => p.color === c) && c !== newPartyColor
      );
      if (nextColor) {
        setNewPartyColor(nextColor);
      }
      
      setError(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddParty();
    }
  };

  const handleRemoveParty = (partyToRemove) => {
    setSelectedParties(prev => prev.filter(p => p.id !== partyToRemove.id));
  };

  const handleContinue = async () => {

    if (selectedParties.length === 0) {
      setError('Please add at least one party to continue.');
      return;
    }

    // Check if all parties have codes
    const partiesWithoutCodes = selectedParties.filter(party => !party.code);
    if (partiesWithoutCodes.length > 0) {
      setError('All parties must have a 3-letter/digit code.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create parties in database
      const createdParties = [];
      
      for (const party of selectedParties) {
        const result = await partyTeamService.createParty(projectId, {
          name: party.name,
          code: party.code,
          color: party.color,
          description: party.description,
          party_type: 'custom'
        });

        if (result.error) {
          throw new Error(`Failed to create party "${party.name}": ${result.error.message}`);
        }

        createdParties.push(result.data);
      }

      onComplete({ 
        processType: 'other',
        parties: createdParties,
        step: 'teams'
      });
    } catch (err) {
      console.error('Error creating parties:', err);
      setError(err.message || 'Failed to create parties');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Inline Party Creation */}
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-4">
          Create your parties (you may have 1 or more):
        </h4>
        
        {/* Inline Add Party Form */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex gap-3 items-end">
            {/* Code Input */}
            <div className="w-20">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code
              </label>
              <input
                type="text"
                value={newPartyCode}
                onChange={(e) => setNewPartyCode(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                placeholder="HTT"
                maxLength={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center font-mono text-sm"
              />
            </div>

            {/* Name Input */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-xs text-gray-500">(max 50 characters)</span>
              </label>
              <input
                type="text"
                value={newPartyName}
                onChange={(e) => setNewPartyName(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter party name (e.g., Investment Firm ABC)"
                maxLength={50}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Color Picker */}
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <div className="flex gap-1 flex-wrap">
                {predefinedColors.map((color) => {
                  const isUsed = selectedParties.some(p => p.color === color);
                  const isSelected = newPartyColor === color;
                  return (
                    <button
                      key={color}
                      type="button"
                      onClick={() => !isUsed && setNewPartyColor(color)}
                      disabled={isUsed}
                      className={`w-8 h-8 rounded-md border-2 transition-all ${
                        isSelected 
                          ? 'border-gray-900 scale-110 shadow-md' 
                          : isUsed
                            ? 'border-gray-200 opacity-30 cursor-not-allowed'
                            : 'border-gray-300 hover:border-gray-500 hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      title={isUsed ? 'Already used' : isSelected ? 'Selected' : 'Click to select'}
                    >
                      {isSelected && (
                        <CheckCircle className="w-4 h-4 text-white mx-auto" strokeWidth={3} />
                      )}
                      {isUsed && !isSelected && (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-4 h-0.5 bg-gray-600 rotate-45"></div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Add Button */}
            <button
              onClick={handleAddParty}
              disabled={!newPartyName.trim() || !newPartyCode.trim()}
              className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Selected Parties Display */}
      <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">
            Your parties:
          </h4>
          {selectedParties.length > 0 ? (
            <div className="space-y-3">
              {selectedParties.map((party) => (
                <div
                  key={party.id}
                  className="flex items-center justify-between border border-gray-200 rounded-lg p-3"
                  style={{ backgroundColor: `${party.color}15` }} // 15% opacity of party color
                >
                  <div className="flex items-center">
                    <div 
                      className="w-4 h-4 rounded-full mr-3 flex-shrink-0"
                      style={{ backgroundColor: party.color }}
                    />
                    <div className="flex items-center gap-3">
                      <span 
                        className="px-2 py-1 text-xs font-bold rounded"
                        style={{ 
                          backgroundColor: party.color,
                          color: 'white'
                        }}
                      >
                        {party.code}
                      </span>
                      <div>
                        <h5 className="font-medium text-gray-900">{party.name}</h5>
                        <p className="text-sm text-gray-500">{party.description}</p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveParty(party)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <Building2 className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No parties added yet</p>
              <p className="text-sm text-gray-400">Use the form above to add parties</p>
            </div>
          )}
        </div>

      {/* Selected Parties Summary */}
      {selectedParties.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-blue-900 mb-3">
            Selected Parties ({selectedParties.length})
          </h4>
          <div className="space-y-2">
            {selectedParties.map((party) => (
              <div key={`${party.id}_${party.type}`} className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-2" />
                  <span className="text-blue-900 font-medium">{party.name}</span>
                  {party.type === 'custom' && (
                    <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                      Custom
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveParty(party)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Continue Button */}
      <div className="flex justify-end pt-6 border-t">
        <button
          onClick={handleContinue}
          disabled={selectedParties.length === 0 || loading || selectedParties.some(p => !p.code)}
          className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating Parties...
            </>
          ) : (
            <>
              Continue to Teams
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PartySetupStep;
